/**
 * components/layout/AuthGuard.tsx — Stage FW-3 (httpOnly-cookie auth, step 3)
 *
 * Client-side route guard with session restoration via httpOnly cookie.
 * On mount, if in-memory tokens are empty and not on /login, attempts a
 * cookie-based refresh + /auth/me/ BEFORE redirecting to /login.
 *
 * Logic:
 * - On mount: if unauthenticated AND not on /login → attempt cookie refresh
 * - If refresh succeeds → restore session (setAuth), no redirect
 * - If refresh fails → redirect to /login
 * - If authenticated AND on /login → redirect to /
 * - Otherwise, render children
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useAuthStore } from '@/lib/store/auth-slice';
import { get, refreshTokens } from '@/lib/api/client';
import type { User } from '@/lib/types';

export function AuthGuard({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isAuthenticated, setAuth } = useAuthStore();
  const [restoring, setRestoring] = useState(true);

  // On mount: attempt cookie-based session restoration
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // If already authenticated or on /login (or callback), skip restoration
      // The callback page handles its own auth flow
      if (isAuthenticated || pathname === '/login' || pathname === '/login/callback') {
        setRestoring(false);
        return;
      }

      // Try cookie-based refresh to restore session after page reload
      try {
        const { access, refresh } = await refreshTokens(); // cookie-based
        const user = await get<User>('/api/v1/auth/me/');
        if (!cancelled) {
          setAuth({ access, refresh, user });
        }
      } catch {
        // Refresh failed → redirect to /login
        if (!cancelled) {
          navigate('/login', { replace: true });
        }
      } finally {
        if (!cancelled) {
          setRestoring(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount

  // Post-restore redirect logic (existing behavior for state changes)
  useEffect(() => {
    if (restoring) return;

    // If on /login and authenticated, redirect to home
    if (pathname === '/login') {
      if (isAuthenticated) {
        navigate('/', { replace: true });
      }
      return;
    }

    // Skip auth check for callback page - it handles its own auth flow
    if (pathname === '/login/callback') {
      return;
    }

    // For all other routes, require authentication
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, pathname, navigate, restoring]);

  // If on /login, only render if unauthenticated
  if (pathname === '/login') {
    if (isAuthenticated) return null; // Hold until redirect
    return <>{children}</>;
  }

  // Always render callback page - it handles its own auth flow
  if (pathname === '/login/callback') {
    return <>{children}</>;
  }

  // For protected routes: hold rendering while restoring or not authenticated
  if (restoring) return null; // or a loading spinner while restoring
  if (!isAuthenticated) return null; // Hold until redirect

  return <>{children}</>;
}
